import { useRef } from 'react';
import { Edit, LogOut, X } from 'lucide-react';
import type { HistoricoMesa, MapaMesa } from '../../types';
import { formatHora } from '../../utils/mesaEstado';

interface OcupadaModalProps {
  mesa: MapaMesa;
  onClose: () => void;
  onSaiu: () => void;
  onEditar: (historico: HistoricoMesa) => void;
}

export function OcupadaModal({ mesa, onClose, onSaiu, onEditar }: OcupadaModalProps) {
  const h = mesa.ultimoHistorico!;
  // Só fecha com clique "verdadeiro" na overlay (mousedown+mouseup fora),
  // para não fechar ao arrastar para fora a partir do conteúdo.
  const overlayRef = useRef<HTMLDivElement>(null);
  const pressStartedOnOverlay = useRef(false);
  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={(e) => {
        pressStartedOnOverlay.current = e.target === overlayRef.current;
      }}
      onMouseUp={(e) => {
        if (e.target !== overlayRef.current) pressStartedOnOverlay.current = false;
      }}
      onClick={(e) => {
        if (e.target === overlayRef.current && pressStartedOnOverlay.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ocupada-title"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 id="ocupada-title" className="modal-title">
            Mesa {mesa.numero} — Ocupada
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Mesa</span>
              <span className="detail-value">
                Mesa {mesa.numero} — {mesa.descricao}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Cliente</span>
              <span className="detail-value">
                {h.cliente?.nome || h.contatoReserva || '—'} {h.cliente?.telefone ? `· ${h.cliente.telefone}` : ''}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Hora</span>
              <span className="detail-value">{formatHora(h.dataMesa)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Pessoas</span>
              <span className="detail-value">{h.numeroPessoas} pax</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Valor</span>
              <span className="detail-value">€{Number(h.valor ?? 0).toFixed(2)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Contacto</span>
              <span className="detail-value">{h.contatoReserva || h.cliente?.telefone || '—'}</span>
            </div>
            {h.notasMesa && (
              <div className="detail-item detail-full">
                <span className="detail-label">Notas</span>
                <span className="detail-value">{h.notasMesa}</span>
              </div>
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button className="btn btn-ghost" onClick={() => onEditar(h)}>
            <Edit className="w-4 h-4" />
            Alterar dados
          </button>
          <button className="btn btn-primary" onClick={onSaiu}>
            <LogOut className="w-4 h-4" />
            Cliente saiu — libertar mesa
          </button>
        </footer>
      </div>
    </div>
  );
}
